import { XCircle, CheckCircle2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const WarumAIAgentsSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tArray } = useLanguage();
  const problems = tArray("aiAgent.warum.problems");
  const solutions = tArray("aiAgent.warum.solutions");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/50 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("aiAgent.warum.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
              {t("aiAgent.warum.title")}
            </h2>

            <p className="text-lg text-muted-foreground mb-4 max-w-3xl mx-auto">
              {t("aiAgent.warum.intro1")}
            </p>

            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t("aiAgent.warum.intro2")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div
              className={cn(
                "bg-background rounded-2xl p-8 border border-border transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-orange/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-chathero-orange" />
                </span>
                {t("aiAgent.warum.problemTitle")}
              </h3>
              <ul className="space-y-4">
                {problems.map((problem, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{
                      transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms",
                      transitionDuration: "400ms",
                    }}
                  >
                    <XCircle className="w-5 h-5 text-chathero-orange flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={cn(
                "bg-primary rounded-2xl p-8 transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary-foreground mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-mint/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint" />
                </span>
                {t("aiAgent.warum.solutionTitle")}
              </h3>
              <ul className="space-y-4">
                {solutions.map((solution, index) => (
                  <li
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                    )}
                    style={{
                      transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms",
                      transitionDuration: "400ms",
                    }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-chathero-mint flex-shrink-0 mt-0.5" />
                    <span className="text-primary-foreground/90">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div
            className={cn(
              "bg-chathero-mint/10 border-l-4 border-chathero-mint rounded-r-xl p-6 md:p-8 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-xl md:text-2xl font-semibold text-primary">
              {t("aiAgent.warum.highlightTitle")}
            </p>
            <p className="text-muted-foreground mt-3">{t("aiAgent.warum.highlight1")}</p>
            <p className="text-muted-foreground mt-2">{t("aiAgent.warum.highlight2")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WarumAIAgentsSection;
